import { createSelector } from "reselect";
import moment from "moment";
import omit from "lodash/fp/omit";
import has from "lodash/has";
import merge from "lodash/fp/merge";
import getOr from "lodash/fp/getOr";
import last from "lodash/fp/last";

import { extractShiftTimesText } from "../shared";
import { DATE_FORMAT } from "../selectors";

// Map props to formTitle
export const getFormTitle = createSelector(
  [props => props.shift],
  extractShiftTimesText
);

// Handle time select change
export const useUpdateTime = createSelector(
  [props => props.handleShiftUpdate, props => props.shift],
  (handleShiftUpdate, shift) => (dateType, currentDate, updateVal) => {
    const [hour, minute] = updateVal?.split(":");

    const timekey = moment(currentDate.startTime).format(DATE_FORMAT);

    let startTime = moment(currentDate.startTime).clone();

    let endTime = moment(currentDate.endTime).clone();

    if (dateType === "startTime") {
      startTime = startTime.set({ hour, minute, second: 0, millisecond: 0 });
      let duration = moment.duration(endTime.diff(startTime));

      // Don't allow update if new date means shift is longer than 24h
      if (duration.asDays() > 1) {
        endTime = moment(currentDate.endTime).clone();
        startTime = moment(currentDate.startTime).clone();
      }

      // Don't allow update if new date means endTime is before starTime
      if (endTime <= startTime) {
        endTime = moment(currentDate.endTime).clone();
        startTime = moment(currentDate.startTime).clone();
      }
    }

    // Updated endTime
    if (dateType === "endTime") {
      endTime = endTime.set({ hour, minute, second: 0, millisecond: 0 });
      let duration = moment.duration(endTime.diff(startTime));

      // If new date makes shift longer than 24h, subtract a day
      if (duration.asDays() > 1) {
        endTime.subtract(1, "days");
      }

      // endTime is before startTime, add a day
      if (endTime < startTime) {
        endTime.add(1, "days");
      }
    }

    const prevTimes = shift?.times;

    handleShiftUpdate({
      times: {
        ...prevTimes,
        [timekey]: {
          ...prevTimes[timekey],
          startTime,
          endTime
        }
      }
    });
  }
);

// Map state to props<shift>
export const mapStateToProps = ({ scheduler: { shiftForm } }) => {
  const shift = shiftForm.shifts[shiftForm.shiftOpenIndex];
  const times = getOr({}, "times", shift);

  const selectedDates = Object.values(times).sort((a, b) =>
    moment(a.startTime).diff(b.startTime, "days")
  );

  return {
    shift,
    shiftForm,
    selectedDates
  };
};

const cloneDates = (startTime, endTime, date, breakMinutes) => ({
  startTime: moment(startTime)
    .clone()
    .set("year", date.year())
    .set("month", date.month())
    .set("date", date.date()),
  endTime: moment(endTime)
    .clone()
    .set("year", date.year())
    .set("month", date.month())
    .set("date", date.date())
    .add(moment(startTime).isSame(moment(endTime), "day") ? 0 : 1, "days"),
  breakMinutes
});

// Map dispatch to props<toggleDate, toggleLinkedShifts>
export const mapDispatchToProps = (dispatch, props) => ({
  toggleDate: date => {
    dispatch((_, getState) => {
      const state = getState();
      const { shiftForm } = state.scheduler;

      const formattedDate = date.format(DATE_FORMAT);
      const shift = shiftForm.shifts[shiftForm.shiftOpenIndex];
      const times = getOr({}, "times", shift);

      // Get last shift time to duplicate
      const { startTime, endTime } = last(Object.values(times)) || {
        startTime: state.scheduler.initialPostStart,
        endTime: state.scheduler.initialPostEnd
      };

      props.handleShiftUpdate({
        times: {
          ...(has(times, formattedDate)
            ? omit(formattedDate, times)
            : merge(times, {
                [formattedDate]: cloneDates(
                  startTime,
                  endTime,
                  date,
                  props?.defaultBreakMinutes
                )
              }))
        }
      });
    });
  },
  setEndRangeDate: date => {
    dispatch((_, getState) => {
      const state = getState();
      const { shiftForm } = state.scheduler;

      const shift = shiftForm.shifts[shiftForm.shiftOpenIndex];
      const times = getOr({}, "times", shift);

      const lastShift = last(Object.values(times));
      const { startTime, endTime } = lastShift || {
        startTime: state.scheduler.initialPostStart,
        endTime: state.scheduler.initialPostEnd
      };

      if (!lastShift) {
        return;
      }

      let isReverse;
      let diffDays = date.diff(startTime, "days") + 1;

      if (diffDays <= 0) {
        diffDays = diffDays * -1 + 1;
        isReverse = true;
      }

      const formattedDate = date.format(DATE_FORMAT);

      const update = Array.from(Array(diffDays)).reduce(
        (agg, _, index) => {
          const baseDate = moment(startTime).clone();
          const changedDate = isReverse
            ? baseDate.subtract(index, "days")
            : baseDate.add(index, "days");
          const key = changedDate.format(DATE_FORMAT);

          return {
            ...agg,
            [key]: cloneDates(startTime, endTime, changedDate)
          };
        },
        {
          [formattedDate]: cloneDates(startTime, endTime, date)
        }
      );

      props.handleShiftUpdate({
        times: merge(times, update)
      });
    });
  }
});
